import { getRolesKey, useCreateRole } from 'api-hooks/role';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ROLE,
  SIZE,
} from 'baseui/modal';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import HOCInput from 'components/common/hoc-input';
import notification from 'components/common/notification';
import { Form, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import Text from 'components/elements/text';
import { SeparatorHorizontal } from 'components/styles';
import { DialogCommonStyles } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

interface Props {
  onClose: () => void;
  onPositivePress: () => void;
  previousValues: {
    name: string;
    permissions: string[];
  };
}

export default function RoleDuplicateDialog(props: Props) {
  const { onClose, onPositivePress, previousValues } = props;
  const { t } = useTranslation();
  const { mutateAsync } = useCreateRole();

  const methods = useForm({
    defaultValues: {
      type: 'replace',
      name: '',
      permissions: previousValues.permissions,
    },
  });

  const onSubmit = async (values) => {
    try {
      delete values.type;
      const result = await mutateAsync(values);
      queryClient.refetchQueries([getRolesKey()[0]]);
      notification.success({ message: result?.message });
      onClose();
    } catch (error) {
      formSetErrors(error.errors, methods.setError);
    }
  };

  return (
    <Modal
      closeable
      isOpen
      animate
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      onClose={onClose}
      size={SIZE.default}
      role={ROLE.dialog}
      overrides={DialogCommonStyles}
    >
      <Form methods={methods} onSubmit={onSubmit}>
        <ModalHeader>{t('role:save_role')}</ModalHeader>
        <ModalBody>
          <Text variant="ParagraphSmall">
            {t('role:replace_or_save_as_new')}
          </Text>
          <Input
            type="radio-group"
            name="type"
            options={[
              {
                children: t('role:replace_role_extra', {
                  extra: previousValues.name,
                }),
                value: 'replace',
              },
              { children: t('role:save_as_new_role'), value: 'new' },
            ]}
          />
          <HOCInput keys={['type']}>
            {(value) =>
              value.type === 'new' ? (
                <Input
                  type="text"
                  name="name"
                  label={t('common:role_name')}
                  placeholder={t('common:role_name')}
                  required
                />
              ) : (
                <></>
              )
            }
          </HOCInput>
        </ModalBody>
        <ModalFooter $style={{ display: 'flex', justifyContent: 'end' }}>
          <Button buttonType="tertiary" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <SeparatorHorizontal $margin={4} />
          <HOCInput keys={['type']}>
            {(value) =>
              value.type === 'replace' ? (
                <Button onClick={onPositivePress}>{t('common:save')}</Button>
              ) : (
                <Input type="submit" />
              )
            }
          </HOCInput>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
